import React, { useState } from 'react';
import { FileText, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import { useIntakeRuns } from '../hooks/useIntakeRuns';
import IntakeReviewPanel from '../components/intake/IntakeReviewPanel';
import IntakeReviewQueue from '../components/intake/IntakeReviewQueue';
import IntakeZone from '../components/intake/IntakeZone';

const STATUS_TONE = {
  planned: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-800',
};

const TYPE_TONE = {
  matricula_imovel: 'bg-sky-100 text-sky-800',
  contrato_social: 'bg-indigo-100 text-indigo-800',
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function IntakePage() {
  const { data, loading, error, refetch } = useIntakeRuns();
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Intake" subtitle="Histórico de cadastro AI-native" icon={FileText} />
      <main className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4">
        {/* Intake-2 (ADR-013): Fila de revisão de operações/fatos propostos */}
        <IntakeReviewQueue />

        {/* Onda 9A abr/26: upload genérico sem entityType — auto-detect document_type */}
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <IntakeZone
            entityType={null}
            entityId={null}
            defaultDocType=""
            onComplete={refetch}
            title="Novo intake (auto-detectar tipo)"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Cada run representa um documento extraído pelo agent-intake-document.
          </div>
          <button
            onClick={refetch}
            className="text-xs px-2 py-1 bg-white border border-slate-200 text-slate-700 rounded hover:bg-slate-50 flex items-center gap-1"
          >
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>

        {loading && <LoadingSpinner label="Carregando histórico..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && (!data || data.length === 0) && (
          <EmptyState
            message="Nenhuma run registrada"
            hint="Use o botão 'Extrair e analisar' na ficha do imóvel ou da empresa para começar."
            icon={FileText}
          />
        )}

        {!loading && !error && data && data.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Extraídas</th>
                  <th className="px-3 py-2 text-right">Matched</th>
                  <th className="px-3 py-2 text-right">Criadas</th>
                  <th className="px-3 py-2 text-right">Revisão</th>
                  <th className="px-3 py-2 text-right">Ações</th>
                  <th className="px-3 py-2 text-left">Quem</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r) => {
                  const isOpen = expanded === r.id;
                  const queued = Number(r.entities_queued) || 0;
                  const completed = r.pipeline_status === 'completed';
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${isOpen ? 'bg-blue-50/40' : ''}`}
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                      >
                        <td className="px-3 py-1.5 text-slate-400">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${TYPE_TONE[r.document_type] || 'bg-slate-100 text-slate-700'}`}>
                            {r.document_type || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_TONE[r.pipeline_status] || 'bg-slate-100 text-slate-700'}`}>
                            {r.pipeline_status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{r.entities_extracted ?? 0}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{r.entities_matched ?? 0}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-blue-700">{r.entities_created ?? 0}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${queued > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                          {queued}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                          {r.actions_approved ?? 0} / {r.actions_total ?? 0}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 truncate max-w-[140px]" title={r.initiated_by || ''}>{r.initiated_by || '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={10} className="px-3 py-3 bg-slate-50/70 border-t border-slate-100">
                            <IntakeReviewPanel
                              intakeRunId={r.id}
                              readonly={completed}
                              onComplete={() => { setExpanded(null); refetch(); }}
                              onCancel={() => setExpanded(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
